import { useEffect } from 'react';
import { adresseDeConnexion } from '../fournisseurs/client';

/**
 * **CE QUI REMPLACE L'ÉCRAN DE CONNEXION DE LA PEAU, ET IL N'Y EN A PAS D'AUTRE.**
 *
 * La peau livre `login-page.tsx`, un formulaire complet. **On ne l'utilise pas**, et c'est
 * la seule pièce des 117 qu'on écarte — elle reste recopiée, pour que l'empreinte de la
 * peau se compare à l'amont, et `<Admin loginPage={…}>` la remplace.
 *
 * `CDC-5` F-55 : cette surface « **s'atteint par la page de connexion unique du site
 * vitrine, celle des quatre rôles**. **Aucune adresse cachée, aucun sélecteur de rôle** ».
 * `CDC-6` F-71 y place cet écran, page 6 de sa liste fermée de huit. Et le motif, écrit dans
 * `site/` : « **une seconde route réservée à l'administration aurait suffi à trahir quelles
 * adresses en sont** ».
 *
 * Un formulaire de connexion ici serait donc **un second écran de connexion, sur un second
 * domaine, pour un produit dont le corpus dit qu'il n'en a qu'un** — et le seul qui trahirait
 * quelles adresses sont d'administration.
 *
 * **Le renvoi ne transporte rien.** F-71 : « à travers cette page, ce qui est retenu est une
 * **DESTINATION, JAMAIS UNE VALEUR QUI VAUT ACCÈS**. Sans cette borne, la page de connexion
 * deviendrait un endroit où une valeur d'accès attend, **sur le domaine public**. »
 *
 * **Il vit dans un effet, pas dans le corps du composant** : un `window.location.replace`
 * appelé pendant le rendu part deux fois sous `StrictMode`, et le second remplace une
 * adresse déjà quittée.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **ET SANS `VITE_SITE_URL`, IL NE REDIRIGE PAS : IL LE DIT.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * **Constaté en recette le 20260824, et c'était une boucle infinie.** L'application était
 * déployée **sans aucune variable de build** : `VITE_API_URL` tombait sur son repli local,
 * donc `checkAuth` échouait faute d'API ; `ra-core` renvoyait sur `/login` ; cette page
 * redirigeait sur `'/'`, l'ancien repli de `adresseDeConnexion()` ; `checkAuth` échouait
 * encore — **et le navigateur rechargeait sans fin entre `/` et `/#/login`**.
 *
 * **Rien ne nommait la cause.** Ni erreur, ni message, ni trace : un écran qui clignote, et
 * une variable de build manquante qu'il fallait deviner. C'est précisément le défaut que
 * `client.ts` décrit pour `VITE_API_URL` — « l'erreur désigne une route absente au lieu
 * d'une variable manquante » — et je l'avais reproduit en pire, parce qu'une boucle
 * n'affiche même pas d'erreur.
 *
 * **Le repli est donc supprimé, et l'absence se dit.** `adresseDeConnexion()` rend `null`,
 * le compilateur force à traiter ce cas, et cette page **nomme la variable** au lieu de
 * rediriger nulle part. Un écran laid qui dit quoi faire vaut mieux qu'un écran qui boucle.
 */
export function RenvoiVersLaConnexion() {
  const adresse = adresseDeConnexion();

  useEffect(() => {
    // **La garde est DANS l'effet aussi**, et pas seulement au rendu : sans elle, un
    // `replace(null)` partirait à la première exécution.
    if (adresse !== null) window.location.replace(adresse);
  }, [adresse]);

  if (adresse === null) {
    return (
      <main className="flex h-svh flex-col items-center justify-center gap-3 p-8 text-center">
        <h1 className="text-lg font-medium">Cette surface est mal configurée.</h1>
        <p className="text-muted-foreground max-w-prose text-sm">
          La variable de construction <code className="font-mono">VITE_SITE_URL</code> est
          absente. Elle porte l’adresse du site vitrine, où vit la page de connexion — cette
          surface n’en a aucune. Sans elle, il n’y a nulle part où vous envoyer.
        </p>
        <p className="text-muted-foreground max-w-prose text-sm">
          C’est une <strong>variable de build</strong>, pas d’exécution : elle est gravée dans
          le paquet à la compilation. La poser demande une reconstruction, jamais un
          redémarrage.
        </p>
      </main>
    );
  }

  return (
    <main className="flex h-svh items-center justify-center p-8">
      <p className="text-muted-foreground text-sm">
        Redirection vers la page de connexion…
      </p>
    </main>
  );
}
